import {
	Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges,
	ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { debounceTime, Subscription } from 'rxjs';
import { MatDatepickerModule } from '@copo/mat-datepicker-new';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgClass, NgIf, NgFor } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { ShipToDetails } from '../../../order/models/ship-to-details';
import { CopoDatepickerInputDirective } from '../../../shared/directives/date-picker.directive';
import { ECallOffService } from '../../services/ecall-off.service';
import { EcallOffShipToDetails } from '../../../order/models/ecall-off-ship-to-details';

export interface InfoEntryCalloffHeaderForm {
	fromDate?: Date;
	requestedDate?: Date;
	toDate?: Date;
	unloadingReference: string;
	shipTo: EcallOffShipToDetails;
}

@Component({
	encapsulation: ViewEncapsulation.Emulated,
	selector: 'copo-infoentry-calloff-header',
	styleUrl: './infoentry-calloff-header.component.css',
	templateUrl: './infoentry-calloff-header.component.html',
	imports: [
		FormsModule,
		ReactiveFormsModule,
		TranslateModule,
		NgClass,

		MatFormFieldModule,

		MatInputModule,

		MatButtonModule,
		MatIconModule,
		NgIf,

		MatSelectModule,

		MatOptionModule,
		NgFor,
		CopoDatepickerInputDirective,
		MatDatepickerModule,
	],
})
export class InfoEntryCalloffHeaderComponent implements OnChanges, OnDestroy, OnInit {
	@HostBinding('class.passthrough') readonly passthrough = true;
	@Input() rddMode?: string = '';
	@Input() shipToDetails?: EcallOffShipToDetails[];
	@Input() selectedShipTo?: EcallOffShipToDetails;
	@Input() unloadingRef?: string = '';
	@Output() readonly applyAll = new EventEmitter<InfoEntryCalloffHeaderForm>();
	@Output() readonly formChange = new EventEmitter<InfoEntryCalloffHeaderForm>();
	@Input() allowChangeOfShipto = false;
	currentDate = moment().toDate();
	formGroup: FormGroup;
	formSubscription: Subscription;
	oneDate = false;
	twoDates = false;

	constructor(

		readonly dialog: MatDialog,
		private readonly ecallOffService: ECallOffService,
	) {
		this.formGroup = new FormGroup({
			unloadingReference: new FormControl(''),
			requestedDate: new FormControl(),
			fromDate: new FormControl(),
			toDate: new FormControl(),
			shipTo: new FormControl<ShipToDetails | null>(null),
			mill: new FormControl('')
		});
		this.formSubscription = this.formGroup.valueChanges
			.pipe(debounceTime(500))
			.subscribe(form => {
				this.formChange.emit(<InfoEntryCalloffHeaderForm> { ...form });
			});
	}

	ngOnChanges(changes: SimpleChanges): void {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		if (changes['rddMode']) {
			this.handleDatesVisibility(<string> changes['rddMode'].currentValue);
		}
	}

	compareShipTo(shipTo1: EcallOffShipToDetails, shipTo2: EcallOffShipToDetails): boolean {
		return shipTo1.shipTo === shipTo2.shipTo;
	}

	ngOnInit() {
		this.formGroup.patchValue({
			unloadingReference: this.unloadingRef,
			shipTo: this.selectedShipTo
		});
	}

	ngOnDestroy(): void {
		this.formSubscription.unsubscribe();
	}

	handleDatesVisibility(value?: string) {
		switch (value) {
		case 'EARLIEST':
		case 'EXACT':
		case 'LATEST':
			this.oneDate = true;
			this.twoDates = false;
			break;
		case 'BETWEEN':
			this.oneDate = false;
			this.twoDates = true;
			break;
		default:
			this.oneDate = false;
			this.twoDates = false;
			break;
		}
	}

	onApplyAll() {
		this.applyAll.emit(<InfoEntryCalloffHeaderForm> { ...this.formGroup.value });
	}

	dateFilter = (d: Date | null): boolean => this.ecallOffService.dateFilter(d);

	getMinToDate() {
		const fromDate = <Date> this.formGroup.get('fromDate')?.value;
		return this.ecallOffService.getMinToDate(fromDate);
	}

	getMaxFromDate() {
		const toDate = <Date> this.formGroup.get('toDate')?.value;
		return this.ecallOffService.getMaxFromDate(toDate);
	}
}
